import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import {
  FormGroup,
  FormArray,
  FormControl,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  EligibilityAssessment,
  EligibilityAssessmentStatus,
  EligibilityRuleAvaluationStatus,
  EligibilityRuleEvaluation,
  OrderApprovalPrerequisites,
  OrderStatus,
} from '../../../../../models/order.model';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { OrderService } from '../../../../../services/order.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { TagStatusComponent } from '../../../../../../shared/components/tag-status/tag-status.component';
import { AccordionModule } from 'primeng/accordion';
import { OrderDetailsStateService } from '../../services/order-details-state.service';
import { distinctUntilChanged, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type DecisionValue =
  | EligibilityRuleAvaluationStatus.Approved
  | EligibilityRuleAvaluationStatus.Rejected;

type RuleForm = FormGroup<{
  id: FormControl<string>;
  ruleName: FormControl<string>;
  decision: FormControl<DecisionValue | null>;
  note: FormControl<string>;
}>;

@Component({
  selector: 'app-order-assessment',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    RadioButtonModule,
    DialogModule,
    TooltipModule,
    TextareaModule,
    ButtonModule,
    TagStatusComponent,
    AccordionModule,
  ],
  templateUrl: './order-assessment.component.html',
  styles: `
    ::ng-deep {
      .assessment-rules {
        .p-accordionpanel {
          border: 0;
        }

        .p-accordionheader {
          padding: 15px 0 !important;
        }

        .p-accordioncontent-content {
          padding: 0 !important;
        }
      }
    }
  `,
})
export class OrderAssessmentComponent implements OnInit {
  orderId = input<string | null>();
  orderStatus = input<OrderStatus | null>();
  eligibilityAssessment = input<EligibilityAssessment | null>(null);
  customerName = input<string | null>(null);
  approvalPrerequisites = input<OrderApprovalPrerequisites | null>(null);
  @Output() reloadOrder = new EventEmitter();

  private fb = inject(FormBuilder);
  private orderService = inject(OrderService);
  private messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly orderDetailsState = inject(OrderDetailsStateService, {
    optional: true,
  });

  rulesForm: FormGroup = this.fb.group({
    id: this.eligibilityAssessment()?.id,
    rules: new FormArray<RuleForm>([]),
    note: [null],
  });

  ruleDialogForm: FormGroup = this.fb.group({
    note: [null],
    decision: [null, Validators.required],
  });

  assessmentDialogForm: FormGroup = this.fb.group({
    orderId: [this.orderId(), Validators.required],
    note: null,
  });

  ruleDialogVisible = signal(false);
  saveAssessmentDialogVisible = signal(false);
  assessmentResultDialogVisible = signal(false);

  dialogRuleId: string | null = null;
  dialogDecision: DecisionValue | null = null;
  dialogNote: string | null = null;
  selectedRuleName: string | null = null;

  assessmentStatusDescription = computed<string>(() => {
    if (!this.eligibilityAssessment()) return '';

    switch (this.eligibilityAssessment()!.status) {
      case EligibilityAssessmentStatus.Pending:
        return 'Pendente';

      case EligibilityAssessmentStatus.InProgress:
        return 'Em andamento';

      case EligibilityAssessmentStatus.Approved:
        return 'Aprovado';

      case EligibilityAssessmentStatus.Rejected:
        return 'Reprovado';

      default:
        return '';
    }
  });

  totalRules = computed(() => {
    return this.assessmentRules().length;
  });

  totalAnsweredRules = computed(() => {
    return this.assessmentRules().filter(
      (x) => x.status !== EligibilityRuleAvaluationStatus.Pending,
    ).length;
  });

  totalNonApprovedRules = computed(() => {
    return this.assessmentRules().filter(
      (x) => x.status !== EligibilityRuleAvaluationStatus.Approved,
    ).length;
  });

  allRulesValuated = computed(() => {
    return this.totalRules() == this.totalAnsweredRules();
  });

  isAssessmentCompleted = computed(() => {
    if (!this.eligibilityAssessment()) return false;

    return [
      EligibilityAssessmentStatus.Approved,
      EligibilityAssessmentStatus.Rejected,
    ].includes(this.eligibilityAssessment()!.status);
  });

  assessmentRules = computed(() => {
    return this.eligibilityAssessment()?.rulesEvaluations ?? [];
  });

  /**
   * Reasons that prevent the order from being approved. The customer contract
   * can only be generated when there is a contract template for the customer's
   * state and a tax rate for the customer's city/state.
   */
  approvalBlockReasons = computed<string[]>(() => {
    if (this.totalNonApprovedRules() > 0) return [];

    const prerequisites = this.approvalPrerequisites();
    const reasons: string[] = [];

    if (prerequisites?.hasContractTemplate === false) {
      reasons.push(
        'Não existe modelo de contrato cadastrado para o estado do cliente.',
      );
    }

    if (prerequisites?.hasTaxRate === false) {
      reasons.push(
        'Não existe taxa cadastrada para a cidade/estado do cliente.',
      );
    }

    return reasons;
  });

  loading = signal(false);

  elegibilityAssessmentCompleted = signal(false);

  RuleStatus = EligibilityRuleAvaluationStatus;

  EligibilityAssessmentStatus = EligibilityAssessmentStatus;

  private pendingIndex: number | null = null;

  get rulesArray(): FormArray<RuleForm> {
    return this.rulesForm.get('rules') as FormArray;
  }

  get assessmentStatusTag(): string {
    return EligibilityAssessmentStatus[this.eligibilityAssessment()!.status];
  }

  getRuleStatusTag(status: EligibilityRuleAvaluationStatus): string {
    return EligibilityRuleAvaluationStatus[status];
  }

  get assessmentFormNoteValue(): string {
    return this.assessmentDialogForm.get('note')?.value;
  }

  ngOnInit(): void {
    if (this.eligibilityAssessment()) {
      this.syncFromAssessment(this.eligibilityAssessment()!);
    }

    this.orderDetailsState?.eligibilityAssessmentChanged$
      .pipe(
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((assessment) => {
        if (!assessment) return;
        this.syncFromAssessment(assessment);
      });
  }

  openRuleDecisionDialog(index: number) {
    if (this.elegibilityAssessmentCompleted()) return;

    const form = this.rulesArray.at(index) as RuleForm;

    this.pendingIndex = index;
    this.dialogRuleId = form.controls.id.value;
    this.selectedRuleName = form.controls.ruleName.value;

    const decision = [
      this.RuleStatus.Approved,
      this.RuleStatus.Rejected,
    ].includes(Number(form.controls.decision.value))
      ? form.controls.decision.value
      : null;

    this.ruleDialogForm.patchValue({
      decision: decision,
      note: form.controls.note.value ?? '',
    });

    this.ruleDialogVisible.set(true);
  }

  confirmRuleDecision() {
    if (this.pendingIndex === null || !this.ruleDialogForm.value.decision)
      return;

    const decision = this.ruleDialogForm.get('decision')?.value;
    if (decision === null || decision === undefined) return;

    const form = this.rulesArray.at(this.pendingIndex);
    form.controls.decision.setValue(decision);
    form.controls.note.setValue(this.ruleDialogForm.get('note')?.value ?? '');

    this.updateEligibilityAssessmentRule(
      form.value.id!,
      this.ruleDialogForm.value.decision,
      this.ruleDialogForm.value.note,
    );
  }

  closeRuleDecisionDialog() {
    this.resetRuleDialogState();
  }

  openSaveAssessmentDialog(): void {
    this.saveAssessmentDialogVisible.set(true);
  }

  saveAssessment(): void {
    if (this.approvalBlockReasons().length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Atenção',
        detail: 'O pedido não pode ser aprovado. Verifique as pendências.',
        life: 3000,
      });
      return;
    }

    this.completeEligibilityAssessment(this.assessmentDialogForm.value.note);
  }

  cancelSaveAssessment(): void {
    this.saveAssessmentDialogVisible.set(false);
  }

  isRulePeddingAnswer(item: EligibilityRuleEvaluation): boolean {
    return item.status === EligibilityRuleAvaluationStatus.Pending;
  }

  canAvaluateOrder(): boolean {
    if (!this.orderStatus()) return false;

    const orderStatus = this.orderStatus()!;

    return ![OrderStatus.Cancelled, OrderStatus.Rejected].includes(orderStatus);
  }

  getRuleStatusDescription(status: EligibilityRuleAvaluationStatus): string {
    switch (status) {
      case EligibilityRuleAvaluationStatus.Pending:
        return 'Pendente';
      case EligibilityRuleAvaluationStatus.Approved:
        return 'Aprovado';
      case EligibilityRuleAvaluationStatus.Rejected:
        return 'Reprovado';
      default:
        return '';
    }
  }

  private updateEligibilityAssessmentRule(
    ruleId: string,
    decision: DecisionValue,
    note?: string,
  ): void {
    if (!this.eligibilityAssessment()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Atenção',
        detail:
          'Os dados da avaliação não foram encontrados. Atualize a página e tente novamente.',
        life: 3000,
      });
      return;
    }

    this.loading.set(true);

    this.orderService
      .updateEligibilityAssessmentRule(
        this.eligibilityAssessment()!.id,
        ruleId,
        decision,
        note,
      )
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.resetRuleDialogState();
          this.reloadOrder.emit();

          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Avaliação salva com sucesso',
            life: 3000,
          });
        },
        error: (e) => {
          this.loading.set(false);
          this.resetRuleDialogState();

          if (e instanceof HttpErrorResponse && e.status !== 404) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar a avaliação',
              life: 3000,
            });
          }
        },
      });
  }

  private completeEligibilityAssessment(note?: string): void {
    if (!this.eligibilityAssessment()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Atenção',
        detail:
          'Os dados da avaliação não foram encontrados. Atualize a página e tente novamente.',
        life: 3000,
      });
      return;
    }

    this.loading.set(true);

    this.orderService
      .completeEligibilityAssessment(this.eligibilityAssessment()!.id, note)
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.resetAssesstmentDialogState();
          this.elegibilityAssessmentCompleted.set(true);
          this.reloadOrder.emit();

          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail:
              'Pedido avaliado com sucesso. O cliente será notificado em breve.',
            life: 3000,
          });
        },
        error: (e) => {
          this.loading.set(false);
          this.resetAssesstmentDialogState();

          if (e instanceof HttpErrorResponse && e.status !== 404) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar a avaliação',
              life: 3000,
            });
          }
        },
      });
  }

  private rebuildRulesForm(rules: EligibilityRuleEvaluation[]) {
    this.rulesArray.clear();

    for (const rule of rules) {
      const ruleStatus = rule.status;

      const form = this.fb.group({
        id: [rule.id],
        ruleName: rule.assessmentRuleName,
        decision: [
          [this.RuleStatus.Approved, this.RuleStatus.Rejected].includes(
            ruleStatus,
          )
            ? ruleStatus
            : null,
          Validators.required,
        ],
        note: [rule.note],
      }) as RuleForm;
      this.rulesArray.push(form);
    }
  }

  private resetRuleDialogState() {
    this.ruleDialogVisible.set(false);
    this.pendingIndex = null;
    this.dialogRuleId = null;
    this.dialogDecision = null;
    this.selectedRuleName = null;
    this.ruleDialogForm.get('decision')?.setValue(null);
    this.ruleDialogForm.get('note')?.setValue(null);
  }

  private resetAssesstmentDialogState(): void {
    this.assessmentDialogForm.get('note')?.setValue(null);
    this.saveAssessmentDialogVisible.set(false);
  }

  private syncFromAssessment(assessment: EligibilityAssessment): void {
    this.elegibilityAssessmentCompleted.set(
      [
        EligibilityAssessmentStatus.Approved,
        EligibilityAssessmentStatus.Rejected,
      ].includes(assessment.status),
    );

    const rules = (assessment.rulesEvaluations ??
      []) as EligibilityRuleEvaluation[];
    this.rebuildRulesForm(rules);

    this.assessmentDialogForm.get('note')?.setValue(assessment.note);
  }
}
